import { DateTime } from '@ksyos/date-time';
import {
    Accordion,
    AccordionContent,
    AccordionContentIndent,
    AccordionItem,
    AccordionTrigger,
    Box,
} from '@ksyos/design-system';
import { capitalize } from 'lodash';
import type { GroupedList } from '@frontend/components/pages/requests/requestsList/RequestListAccordion';
import { getSelectedLanguage } from '@frontend/translate';

type MonthAccordionProps = {
    children: JSX.Element;
    groupedList: GroupedList;
    month: number | null;
    setMonth: (month: number) => Promise<void> | void;
    year: number;
};

export function MonthAccordion({ month, year, children, setMonth, groupedList }: MonthAccordionProps) {
    const translate = getSelectedLanguage();

    // Safely access the months for the given year
    const monthsForYear = groupedList[year] || {};
    if (!monthsForYear) {
        return null;
    }

    const monthsInReverseChronologicalOrder = Object.entries(monthsForYear).reverse();

    async function handleMonthSelection(value: number) {
        if (month !== value) {
            await setMonth(value);
        }
    }

    const monthAsString = month !== null && month !== undefined ? String(month) : undefined;

    return (
        <Accordion
            collapsible={true}
            defaultValue={monthAsString}
            type="single"
        >
            {monthsInReverseChronologicalOrder.map(([monthString, total]) => {
                const monthAsNumber = Number(monthString);

                return (
                    <AccordionItem
                        key={`${year}-${monthString}`}
                        onClick={() => handleMonthSelection(monthAsNumber)}
                        value={monthString}
                    >
                        <AccordionTrigger icon="records">
                            <Box
                                display="flex"
                                justifyContent="space-between"
                            >
                                {getMonthHeaderText(monthAsNumber, year)}
                                <Box
                                    color="neutral400"
                                    ml={1}
                                >
                                    ({total})
                                </Box>
                            </Box>
                        </AccordionTrigger>
                        <AccordionContent>
                            <AccordionContentIndent>{children}</AccordionContentIndent>
                        </AccordionContent>
                    </AccordionItem>
                );
            })}
        </Accordion>
    );

    function getMonthHeaderText(monthToUse: number, yearToUse: number) {
        const now = DateTime.now();
        const formattedMonth = now.month(monthToUse).format('MMMM');

        if (now.year() === yearToUse && now.month() === monthToUse) {
            return `${translate.common.date_currentMonth}: ${capitalize(formattedMonth)}`;
        }

        return capitalize(formattedMonth);
    }
}
